import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, TextField, MenuItem, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getSsiTechnicalMeta, getSsiTechnicalJobs, getSsiTechnicalJob,
  createSsiTechnicalItem, createSsiBulkGrn,
  saveSsiTechnicalInspection, addSsiPartRequest,
  approveSsiTechnicalStock, updateSsiStockProductName, rejectSsiTechnicalJob, deleteSsiTechnicalJob
} from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import PartPhotoImage from '../../components/PartPhotoImage';
import { isFullAdmin, canDelete } from '../../utils/auth';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDateTime, fmtExportStatus } from '../../utils/tableExport';

const userRole = () => (localStorage.getItem('erp_user_role') || '').toLowerCase().replace(/[\s-]+/g, '_');
const isAdmin = () => userRole() === 'admin' || userRole() === 'super_admin';

const statusColor = (s) => {
  if (s === 'approved') return 'success';
  if (s === 'submitted') return 'warning';
  if (s === 'rejected') return 'error';
  return 'default';
};

const fmtDateTime = (v) => {
  if (!v) return '-';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

const normalizeItem = (val) => {
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    return {
      status: ['good', 'bad'].includes(val.status) ? val.status : '',
      config: val.config != null ? String(val.config) : ''
    };
  }
  if (val === true) return { status: 'good', config: '' };
  return { status: '', config: '' };
};

const parseChecklist = (raw, keys = []) => {
  let src = raw;
  if (typeof src === 'string') {
    try { src = JSON.parse(src); } catch { src = {}; }
  }
  if (!src || typeof src !== 'object') src = {};
  const out = {};
  const list = keys.length ? keys : Object.keys(src);
  list.forEach((k) => { out[k] = normalizeItem(src[k]); });
  return out;
};

const SsiTechnicalList = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [grnOpen, setGrnOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addForm, setAddForm] = useState({ category: '', serial_number: '', model: '', brand: '' });
  const [grnForm, setGrnForm] = useState({ category: '', model: '', brand: '', serial_numbers: '' });

  const load = async () => {
    try {
      setLoading(true);
      const [m, j] = await Promise.all([
        getSsiTechnicalMeta(),
        getSsiTechnicalJobs({
          ...(category ? { category } : {}),
          ...(status ? { status } : {})
        })
      ]);
      setMeta(m.data || { categories: [] });
      setJobs(j.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load SSI Technical jobs'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [category, status]);

  const handleDeleteJob = async (job) => {
    if (!canDelete()) return;
    if (!window.confirm(`Delete SSI job serial ${job.serial_number}?\nThis cannot be undone.`)) return;
    try {
      await deleteSsiTechnicalJob(job.id);
      setSuccess('SSI job deleted');
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete SSI job'));
    }
  };

  const handleAddNew = async () => {
    if (!addForm.category || !addForm.serial_number.trim()) {
      setError('Category and serial number are required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const res = await createSsiTechnicalItem({
        category: addForm.category,
        serial_number: addForm.serial_number.trim(),
        model: addForm.model.trim() || null,
        brand: addForm.brand.trim() || null
      });
      setAddOpen(false);
      setAddForm({ category: '', serial_number: '', model: '', brand: '' });
      setSuccess('Checking item created');
      if (res.data?.id) navigate(`/ssi-technical/${res.data.id}`);
      else load();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create item'));
    } finally {
      setSaving(false);
    }
  };

  const handleNewGrn = async () => {
    if (!grnForm.category || !grnForm.serial_numbers.trim()) {
      setError('Category and bulk serial numbers are required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const res = await createSsiBulkGrn({
        category: grnForm.category,
        model: grnForm.model.trim() || null,
        brand: grnForm.brand.trim() || null,
        serial_numbers: grnForm.serial_numbers
      });
      setGrnOpen(false);
      setGrnForm({ category: '', model: '', brand: '', serial_numbers: '' });
      setSuccess(res.message || 'SSI GRN created');
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create SSI GRN'));
    } finally {
      setSaving(false);
    }
  };

  const serialCount = grnForm.serial_numbers
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>SSI Technical</Typography>
          <Typography color="text.secondary">
            Warranty exchange intake + SSI checking. Exchange jobs from New Request open here (not Workshop).
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ExportButtons
            title="SSI Technical"
            filename="ssi_technical"
            landscape
            columns={[
              { label: 'Category', value: (job) => dash(job.category) },
              { label: 'Serial', value: (job) => dash(job.serial_number) },
              { label: 'Model', value: (job) => dash(job.model) },
              { label: 'Brand', value: (job) => dash(job.brand) },
              { label: 'Service / Batch', value: (job) => dash(job.repair?.service_number || job.repair?.grn_number || job.grn_batch_ref || job.repair_id) },
              { label: 'Technician', value: (job) => dash(job.inspected_by_name) },
              { label: 'Completed', value: (job) => fmtExportDateTime(job.submitted_at) },
              { label: 'Status', value: (job) => fmtExportStatus(job.status) }
            ]}
            rows={jobs}
            onError={setError}
          />
          <Button variant="contained" color="primary" onClick={() => setGrnOpen(true)}>
            NEW GRN
          </Button>
          <Button variant="outlined" onClick={() => setAddOpen(true)}>
            Add New
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <MenuItem value="">All categories</MenuItem>
              {(meta.categories || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="outlined" onClick={load}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <CircularProgress /> : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Serial</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Service / Batch</TableCell>
                <TableCell>Technician</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>{job.category}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{job.serial_number}</TableCell>
                  <TableCell>{job.model || '-'}</TableCell>
                  <TableCell>
                    {job.repair?.service_number || job.repair?.grn_number || job.grn_batch_ref || job.repair_id || '-'}
                  </TableCell>
                  <TableCell>{job.inspected_by_name || '-'}</TableCell>
                  <TableCell>{fmtDateTime(job.submitted_at)}</TableCell>
                  <TableCell><Chip size="small" label={job.status} color={statusColor(job.status)} /></TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => navigate(`/ssi-technical/${job.id}`)}>
                      {job.status === 'pending' ? 'Open' : 'View'}
                    </Button>
                    {canDelete() && job.status !== 'approved' && (
                      <Button size="small" color="error" onClick={() => handleDeleteJob(job)}>
                        Delete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {jobs.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">No SSI Technical jobs</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add New — single serial */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New — Checking Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select fullWidth required label="Category"
                value={addForm.category}
                onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
              >
                {(meta.categories || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Serial number"
                value={addForm.serial_number}
                onChange={(e) => setAddForm({ ...addForm, serial_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Model"
                value={addForm.model}
                onChange={(e) => setAddForm({ ...addForm, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Brand"
                value={addForm.brand}
                onChange={(e) => setAddForm({ ...addForm, brand: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={handleAddNew}>
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW GRN — bulk serials */}
      <Dialog open={grnOpen} onClose={() => setGrnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>NEW GRN — Bulk Serial Numbers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Creates one SSI Technical job per serial for inspection (Good/Bad checklist).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select fullWidth required label="Category"
                value={grnForm.category}
                onChange={(e) => setGrnForm({ ...grnForm, category: e.target.value })}
              >
                {(meta.categories || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Model"
                value={grnForm.model}
                onChange={(e) => setGrnForm({ ...grnForm, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Brand"
                value={grnForm.brand}
                onChange={(e) => setGrnForm({ ...grnForm, brand: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required multiline minRows={6}
                label="Bulk serial numbers"
                placeholder={'One serial per line\nSN001\nSN002\nSN003'}
                value={grnForm.serial_numbers}
                onChange={(e) => setGrnForm({ ...grnForm, serial_numbers: e.target.value })}
                helperText={`${serialCount} serial(s) detected — separate by new line, comma, or semicolon`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrnOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving || serialCount === 0} onClick={handleNewGrn}>
            {saving ? 'Creating…' : `Create GRN (${serialCount})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const SsiTechnicalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [comment, setComment] = useState('');
  const [partRequests, setPartRequests] = useState([]);
  const [itemPartForms, setItemPartForms] = useState({});
  const [requestingKey, setRequestingKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [labels, setLabels] = useState({});
  const [configHints, setConfigHints] = useState({});
  const [stockProductName, setStockProductName] = useState('');
  const saveTimer = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getSsiTechnicalJob(id);
      const data = res.data;
      setJob(data);
      const keys = data.checklist_keys || Object.keys(parseChecklist(data.checklist));
      setChecklist(parseChecklist(data.checklist, keys));
      setComment(data.technician_comment || '');
      setLabels(data.checklist_labels || {});
      setConfigHints(data.config_hints || {});
      setPartRequests(Array.isArray(data.part_requests) ? data.part_requests : []);
      setStockProductName(
        data.stock_product_name || data.inspection_summary || data.model || ''
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load job'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const keys = useMemo(() => {
    if (job?.checklist_keys?.length) return job.checklist_keys;
    return Object.keys(checklist || {});
  }, [job, checklist]);

  const readOnly = job && ['submitted', 'approved', 'rejected'].includes(job.status);

  const persistChecklist = async (nextChecklist, nextComment = comment, submit = false) => {
    if (readOnly && !submit) return;
    try {
      if (submit) setSaving(true);
      else setAutoSaving(true);
      setError('');
      const res = await saveSsiTechnicalInspection(id, {
        checklist: nextChecklist,
        technician_comment: nextComment,
        serial_number: job.serial_number,
        model: job.model,
        brand: job.brand,
        part_requests: partRequests,
        submit
      });
      const summary = res?.data?.inspection_summary;
      if (submit && summary) {
        setSuccess(`Job completed — ${summary}`);
      } else {
        setSuccess(submit ? 'Job completed — checklist saved' : 'Checklist saved');
      }
      if (submit) {
        await load();
      } else if (res?.data) {
        setJob((prev) => ({
          ...(prev || {}),
          ...res.data,
          checklist_keys: prev?.checklist_keys,
          checklist_labels: prev?.checklist_labels,
          config_hints: prev?.config_hints,
          repair: prev?.repair
        }));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
      setAutoSaving(false);
    }
  };

  const scheduleSave = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistChecklist(next, comment, false), 500);
  };

  const setItemConfig = (key, config) => {
    if (readOnly) return;
    const cur = normalizeItem(checklist[key]);
    const next = { ...checklist, [key]: { ...cur, config } };
    setChecklist(next);
    scheduleSave(next);
  };

  const save = async (submit = false) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (submit) {
      const missing = keys.filter((k) => {
        const st = normalizeItem(checklist[k]).status;
        return st !== 'good' && st !== 'bad';
      });
      if (missing.length) {
        setError(`Mark Good/Bad for: ${missing.map((k) => labels[k] || k.replace(/_/g, ' ')).join(', ')}`);
        return;
      }
    }
    await persistChecklist(checklist, comment, submit);
  };

  const handleAddPartForItem = async (key) => {
    const label = labels[key] || key.replace(/_/g, ' ');
    const form = itemPartForms[key] || {};
    const product_name = (form.product_name || label).trim();
    if (!product_name) {
      setError(`Enter part name for ${label}`);
      return;
    }
    const role = userRole();
    if (role === 'technician' && !form.part_photo) {
      setError(`Upload part photo for ${label}`);
      return;
    }
    try {
      setRequestingKey(key);
      setError('');
      const res = await addSsiPartRequest(id, {
        product_name,
        quantity: form.quantity || 1,
        notes: form.notes || `Needed for ${label}`,
        checklist_key: key,
        checklist_label: label,
        part_photo: form.part_photo || undefined
      });
      setPartRequests(res.data?.part_requests || []);
      setItemPartForms((prev) => ({
        ...prev,
        [key]: { product_name: label, quantity: 1, notes: '', part_photo: '' }
      }));
      setSuccess(`Part requested for ${label}`);
      window.dispatchEvent(new Event('part-request-created'));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to request part'));
    } finally {
      setRequestingKey('');
    }
  };

  const partsForKey = (key) =>
    (partRequests || []).filter((p) => p.checklist_key === key);

  const getItemPartForm = (key) => {
    if (itemPartForms[key]) return itemPartForms[key];
    const label = labels[key] || key.replace(/_/g, ' ');
    return { product_name: label, quantity: 1, notes: '', part_photo: '' };
  };

  const setItemPartPhoto = (key, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setItemPartForms((prev) => ({
        ...prev,
        [key]: { ...getItemPartForm(key), ...prev[key], part_photo: reader.result, part_photo_name: file.name }
      }));
    };
    reader.readAsDataURL(file);
  };

  const setItemPartField = (key, field, value) => {
    setItemPartForms((prev) => ({
      ...prev,
      [key]: { ...getItemPartForm(key), [field]: value }
    }));
  };

  const approve = async () => {
    try {
      if (!stockProductName.trim()) {
        setError('Enter / confirm product name before approve');
        return;
      }
      setSaving(true);
      await updateSsiStockProductName(id, { product_name: stockProductName.trim() }).catch(() => {});
      const res = await approveSsiTechnicalStock(id, { product_name: stockProductName.trim() });
      setSuccess(res.message || 'Approved — item added to inventory');
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Approve failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDetail = async () => {
    if (!canDelete() || !job) return;
    if (!window.confirm(`Delete SSI job serial ${job.serial_number}?\nThis cannot be undone.`)) return;
    try {
      setSaving(true);
      await deleteSsiTechnicalJob(id);
      setSuccess('Deleted');
      navigate('/ssi-technical');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete'));
    } finally {
      setSaving(false);
    }
  };

  const reject = async () => {
    try {
      setSaving(true);
      await rejectSsiTechnicalJob(id, { reason: 'Rejected by admin' });
      setSuccess('Rejected');
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Reject failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (!job) return <Alert severity="error">Job not found</Alert>;

  const goodCount = keys.filter((k) => normalizeItem(checklist[k]).status === 'good').length;
  const badCount = keys.filter((k) => normalizeItem(checklist[k]).status === 'bad').length;

  return (
    <Box maxWidth="960px">
      <Button onClick={() => navigate('/ssi-technical')} sx={{ mb: 2 }}>← Back to SSI Technical</Button>
      <Typography variant="h4" fontWeight={600} gutterBottom>SSI Technical Inspection</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {(job.inspected_by_name || job.submitted_at) && (
        <Alert severity={readOnly ? 'success' : 'info'} sx={{ mb: 2 }}>
          <strong>Technician:</strong> {job.inspected_by_name || job.technician_name || '-'}
          {' · '}
          <strong>Complete date:</strong> {fmtDateTime(job.submitted_at || job.completed_at)}
          {job.grn_batch_ref ? ` · Batch ${job.grn_batch_ref}` : ''}
          {readOnly ? ' · Checklist locked' : ''}
          {autoSaving ? ' · Saving…' : ''}
        </Alert>
      )}

      {job.inspection_summary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Inspection summary:</strong> {job.inspection_summary}
        </Alert>
      )}

      {isAdmin() && ['submitted', 'pending'].includes(job.status) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Product name for stock (Admin can edit before approve)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            On approve: if a product with this exact name exists, serial is added there; otherwise a new product is created.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Product name"
            value={stockProductName}
            onChange={(e) => setStockProductName(e.target.value)}
            helperText="Defaults from inspection summary (Model | Processor | RAM | Storage | Screen | Windows)"
          />
          <Button
            size="small"
            sx={{ mt: 1 }}
            disabled={saving || !stockProductName.trim()}
            onClick={async () => {
              try {
                setSaving(true);
                await updateSsiStockProductName(id, { product_name: stockProductName.trim() });
                setSuccess('Product name saved');
                load();
              } catch (err) {
                setError(getErrorMessage(err, 'Failed to save product name'));
              } finally {
                setSaving(false);
              }
            }}
          >
            Save product name
          </Button>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Card><CardContent><Typography variant="caption">Category</Typography><Typography fontWeight={600}>{job.category}</Typography></CardContent></Card></Grid>
        <Grid item xs={6} md={3}><Card><CardContent><Typography variant="caption">Serial</Typography><Typography fontWeight={600} sx={{ fontFamily: 'monospace' }}>{job.serial_number}</Typography></CardContent></Card></Grid>
        <Grid item xs={6} md={3}><Card><CardContent><Typography variant="caption">Model</Typography><Typography fontWeight={600}>{job.model || '-'}</Typography></CardContent></Card></Grid>
        <Grid item xs={6} md={3}><Card><CardContent><Typography variant="caption">Status</Typography><Chip size="small" label={job.status} color={statusColor(job.status)} /></CardContent></Card></Grid>
      </Grid>

      {job.repair && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Warranty exchange job — not in Workshop.
          {' · '}Exchange out: <strong>{job.repair.exchange_product_name || '-'}</strong>
          {job.repair.exchange_serial_number ? ` (SN ${job.repair.exchange_serial_number})` : ''}
          {' · '}JOB {job.repair.grn_number || job.repair.service_number}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Physical / component checklist</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip size="small" color="success" label={`${goodCount} Good`} />
            <Chip size="small" color="error" label={`${badCount} Bad`} />
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mark each item Good or Bad. Use Configuration for specs (e.g. RAM: 8GB DDR4).
          If Bad, request the needed part for that item below it.
        </Typography>

        {keys.map((key) => {
          const item = normalizeItem(checklist[key]);
          const hint = configHints[key] || 'e.g. 8GB DDR4';
          const label = labels[key] || key.replace(/_/g, ' ');
          const itemParts = partsForKey(key);
          const pf = getItemPartForm(key);
          return (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1.5,
                borderColor: item.status === 'bad' ? 'error.light' : item.status === 'good' ? 'success.light' : 'divider',
                bgcolor: item.status === 'bad' ? 'rgba(211,47,47,0.04)' : 'background.paper'
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontWeight={600} sx={{ minWidth: 140 }}>
                  {label}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={item.status || null}
                  disabled={readOnly}
                  onChange={(_, v) => {
                    if (readOnly) return;
                    const cur = normalizeItem(checklist[key]);
                    const next = { ...checklist, [key]: { ...cur, status: v || '' } };
                    setChecklist(next);
                    scheduleSave(next);
                    if (v === 'bad' && !itemPartForms[key]) {
                      setItemPartForms((prev) => ({
                        ...prev,
                        [key]: { product_name: label, quantity: 1, notes: '' }
                      }));
                    }
                  }}
                >
                  <ToggleButton value="good" color="success" sx={{ px: 2 }}>Good</ToggleButton>
                  <ToggleButton value="bad" color="error" sx={{ px: 2 }}>Bad</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={1}
                maxRows={3}
                sx={{ mt: 1 }}
                label="Configuration"
                placeholder={hint}
                value={item.config}
                disabled={readOnly}
                onChange={(e) => setItemConfig(key, e.target.value)}
              />

              {/* Item-wise part request — only when Bad */}
              {item.status === 'bad' && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="error.main" gutterBottom>
                    Part needed for {label}?
                  </Typography>
                  {itemParts.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {itemParts.map((p, i) => (
                        <Chip
                          key={p.id || i}
                          size="small"
                          color="warning"
                          sx={{ mr: 0.5, mb: 0.5 }}
                          label={`${p.product_name} × ${p.quantity} (${p.status || 'pending'})`}
                        />
                      ))}
                    </Box>
                  )}
                  {!readOnly && (
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Part name"
                          value={pf.product_name}
                          onChange={(e) => setItemPartField(key, 'product_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Qty"
                          value={pf.quantity}
                          onChange={(e) => setItemPartField(key, 'quantity', e.target.value)}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={8} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Notes"
                          value={pf.notes}
                          onChange={(e) => setItemPartField(key, 'notes', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          color="warning"
                          disabled={requestingKey === key}
                          onClick={() => handleAddPartForItem(key)}
                        >
                          {requestingKey === key ? '…' : 'Request Part'}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Button size="small" variant="outlined" component="label">
                          {userRole() === 'technician' ? 'Part Photo *' : 'Part Photo'}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = '';
                              setItemPartPhoto(key, f);
                            }}
                          />
                        </Button>
                        {pf.part_photo_name && (
                          <Typography variant="caption" sx={{ ml: 1 }}>{pf.part_photo_name}</Typography>
                        )}
                        {pf.part_photo && (
                          <Box
                            component="img"
                            src={pf.part_photo}
                            alt="preview"
                            sx={{ mt: 1, maxHeight: 100, borderRadius: 1, display: 'block' }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}

        <Divider sx={{ my: 2 }} />
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Technician comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={readOnly}
        />
      </Paper>

      {/* Summary of all SSI part requests */}
      {partRequests.length > 0 && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>All part requests for this SSI job</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Checklist item</TableCell>
                <TableCell>Part</TableCell>
                <TableCell>Photo</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partRequests.map((p, i) => (
                <TableRow key={p.id || i}>
                  <TableCell>{p.checklist_label || p.checklist_key || '-'}</TableCell>
                  <TableCell>{p.product_name}</TableCell>
                  <TableCell>
                    {p.part_photo_url ? (
                      <PartPhotoImage photoUrl={p.part_photo_url} />
                    ) : '—'}
                  </TableCell>
                  <TableCell>{p.quantity}</TableCell>
                  <TableCell><Chip size="small" label={p.status || 'pending'} /></TableCell>
                  <TableCell>{p.requested_by || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {!readOnly && (
          <>
            <Button variant="outlined" disabled={saving || autoSaving} onClick={() => save(false)}>Save</Button>
            <Button variant="contained" disabled={saving || autoSaving} onClick={() => save(true)}>
              Complete & Submit for Stock Approval
            </Button>
          </>
        )}
        {isAdmin() && job.status === 'submitted' && (
          <>
            <Button color="success" variant="contained" disabled={saving} onClick={approve}>
              Admin Approve — Add to Stock
            </Button>
            <Button color="error" variant="outlined" disabled={saving} onClick={reject}>
              Reject
            </Button>
          </>
        )}
        {canDelete() && job.status !== 'approved' && (
          <Button color="error" variant="outlined" disabled={saving} onClick={handleDeleteDetail}>
            Delete Job
          </Button>
        )}
        {job.status === 'approved' && (
          <Alert severity="success">
            Added to inventory: <strong>{job.stock_product_name || `product #${job.stock_product_id}`}</strong>
            {' '}(serial {job.serial_number})
          </Alert>
        )}
      </Box>
    </Box>
  );
};

const SsiTechnicalPage = () => {
  const { id } = useParams();
  if (id) return <SsiTechnicalDetail />;
  return <SsiTechnicalList />;
};

export default SsiTechnicalPage;
